import { combineReducers } from 'redux';
import * as userConstants from '../constants/user';
import * as organizationConstants from '../constants/organizations';
import * as assetConstants from '../constants/assets';
import * as bucketConstants from '../constants/buckets';
import initialAppsState from '../helpers/InitialAppsState';
import OtherApplicationsPaths from '../helpers/OtherApplicationsPaths';

function users(state = {}, action) {
    switch (action.type) {
        case userConstants.FETCH_ME_SUCCESS:
            const user = action.payload;
            return {
                ...state,
                [user.id]: { ...state[user.id], ...user },
            };
        case organizationConstants.FETCH_ORGANIZATION_MEMBERS_SUCCESS:
        case bucketConstants.FETCH_BUCKET_MEMBERS_SUCCESS:
        case bucketConstants.ADD_BUCKET_MEMBERS_SUCCESS:
        case organizationConstants.ADD_ORGANIZATION_MEMBERS_SUCCESS:
            const { members } = action.payload;
            const newUser = members.reduce((memo, user) => {
                // leave existing fields, only update with new on
                // eg, current user has email, but when retrieved from
                // organization or bucket she doesn't
                memo[user.id] = { ...state[user.id], ...user };
                return memo;
            }, {});
            return {
                ...state,
                ...newUser,
            };
        default:
            return state;
    }
}

function organizations(state = {}, action) {
    switch (action.type) {
        case organizationConstants.FETCH_ORGANIZATIONS_SUCCESS: {
            const organizations = action.payload;
            const newOrganizations = organizations.reduce((memo, org) => {
                memo[org.id] = org;
                return memo;
            }, {});
            return {
                ...state,
                ...newOrganizations,
            };
        }
        case organizationConstants.FETCH_ORGANIZATION_GA_SETTINGS_SUCCESS:
        case organizationConstants.UPDATE_ORGANIZATION_GA_SETTINGS_SUCCESS: {
            const { organizationId, settings } = action.payload;
            return {
                ...state,
                [organizationId]: {
                    ...state[organizationId],
                    ga: settings,
                },
            };
        }
        case organizationConstants.UPDATE_ORGANIZATION_SETTINGS_SUCCESS: {
            const organization = action.payload;
            return {
                ...state,
                [organization.id]: {
                    ...state[organization.id],
                    ...organization,
                },
            };
        }
        case organizationConstants.UPDATE_ORGANIZATION_AVATAR_SUCCESS: {
            const { organizationId, avatars } = action.payload;
            return {
                ...state,
                [organizationId]: {
                    ...state[organizationId],
                    ...avatars,
                },
            };
        }
        case organizationConstants.CURRENT_USER_ROLE_CHANGE_SUCCESS: {
            const { currentUserIsAdmin, organizationId } = action.payload;
            return {
                ...state,
                [organizationId]: {
                    ...state[organizationId],
                    currentUserIsAdmin,
                },
            };
        }
        default:
            return state;
    }
}

function buckets(state = {}, action) {
    switch (action.type) {
        case organizationConstants.FETCH_ORGANIZATION_BUCKETS_SUCCESS:
            const { buckets } = action.payload;
            const newBuckets = buckets.reduce((memo, bucket) => {
                memo[bucket.id] = { ...state[bucket.id], ...bucket };
                return memo;
            }, {});
            return {
                ...state,
                ...newBuckets,
            };
        case organizationConstants.FETCH_BUCKET_SUCCESS: {
            const { bucket } = action.payload;
            return {
                ...state,
                [bucket.id]: { ...state[bucket.id], ...bucket },
            };
        }
        case organizationConstants.NEW_BUCKET_SUCCESS:
        case bucketConstants.UPDATE_BUCKET_SUCCESS:
        case bucketConstants.UPDATE_BUCKET_SETTINGS_SUCCESS: {
            const bucket = action.payload;
            return {
                ...state,
                [bucket.id]: { ...state[bucket.id], ...bucket },
            };
        }
        case organizationConstants.DELETE_BUCKET_SUCCESS: {
            const { bucketId } = action.payload;
            delete state[bucketId];
            return {
                ...state,
            };
        }
        case bucketConstants.UPDATE_BUCKET_AVATAR_SUCCESS: {
            const { bucketId, avatars } = action.payload;
            return {
                ...state,
                [bucketId]: {
                    ...state[bucketId],
                    ...avatars,
                },
            };
        }
        case bucketConstants.CURRENT_USER_ROLE_CHANGE_SUCCESS: {
            const { currentUserIsAdmin, bucketId } = action.payload;
            return {
                ...state,
                [bucketId]: {
                    ...state[bucketId],
                    currentUserIsAdmin,
                },
            };
        }
        default:
            return state;
    }
}

function assets(state = {}, action) {
    switch (action.type) {
        case assetConstants.FETCH_ASSETS_SUCCESS:
        case assetConstants.LOAD_MORE_ASSETS_SUCCESS:
            const { assets } = action.payload;
            const newAssets = assets.reduce((memo, asset) => {
                memo[asset.id] = { ...state[asset.id], ...asset };
                return memo;
            }, {});
            return {
                ...state,
                ...newAssets,
            };
        case assetConstants.UPDATE_ASSET_SUCCESS: {
            const { asset } = action.payload;

            return {
                ...state,
                [asset.id]: {
                    ...asset,
                    hasChangesAfterLastPublish: !!asset.embedURL,
                    updatedAt: new Date(),
                },
            };
        }
        case assetConstants.MOVE_ASSET_SUCCESS:
            const { asset, isCopy, isDuplicate, result } = action.payload;

            if(isDuplicate && result.clonedIds && result.clonedIds.length > 0) {
                const originalAsset = state[result.clonedIds[0].original];
                const newAssetId = result.clonedIds[0].clone;

                const newAsset = {
                    ...originalAsset,
                    id: newAssetId,
                    title: `${originalAsset.title} - Copy`,
                    previewURL: OtherApplicationsPaths.preview[originalAsset.context].getPath({
                        id: newAssetId,
                    }),
                    updatedAt: new Date(),
                    tags: null
                };

                return {
                    [newAssetId]: newAsset,
                    ...state,
                };
            }

            if (!isCopy) {
                delete state[asset.id]
            }

            return state;
        case assetConstants.DELETE_ASSET_SUCCESS: {
            const { asset } = action.payload;
            
            const newState = { ...state };
            delete newState[asset.id];
            
            return newState;
        }
        case assetConstants.PUBLISH_ASSET_SUCCESS: {
            const { asset } = action.payload;

            return {
                ...state,
                [asset.id]: { 
                    ...asset, 
                    hasChangesAfterLastPublish: false,
                    updatedAt: new Date()
                },
            };
        }
        default:
            return state;
    }
}

function applications(state = initialAppsState, action) {
    return state;
}

const dbReducer = combineReducers({
    users,
    organizations,
    buckets,
    applications,
    assets,
});

export default dbReducer;



// WEBPACK FOOTER //
// ./src/reducers/dbReducer.js